import React, { useEffect, useMemo, useState } from "react";

import { LoadingIcon as Loading, Typography } from "@packages/ds";
import { useIntl } from "react-intl";

import { Button } from "ds/Button";
import Checkbox from "ds/Checkbox";
import Modal from "ds/Modal";
import * as Radio from "ds/Radio";
import * as Sticker from "ds/Sticker";
import Tag from "ds/Tag";
import useDecodedParams from "Hooks/useDecodedParams";
import useSelectorWithUrlParams from "Hooks/useSelectorWithUrlParams";
import { organizationEstimateSelector } from "Reducers/organization";
import {
  addonsCurrentSelector,
  loadingAddonsSelector,
  getAddons,
  updateAddons,
  addonsErrorSelector,
  addonsAvailableSelector
} from "Reducers/organization/addons";
import { useTrialInfo } from "Reducers/organization/hooks/useTrialnfo";
import { useAppSelector, useAppDispatch } from "Store/hooks";

import * as S from "./AddonModals.style";

type AddonType = "default" | "standard";

type UserManagementAddonModalProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onUpgrade?: () => void;
};
export const UserManagementAddonModal = ({
  isOpen,
  setIsOpen,
  onUpgrade
}: UserManagementAddonModalProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { organizationId } = useDecodedParams<{
    organizationId: string;
  }>();

  const organizationEstimate = useSelectorWithUrlParams(
    organizationEstimateSelector
  );

  const currentAddons = useSelectorWithUrlParams(addonsCurrentSelector);

  const addonsError = useAppSelector(addonsErrorSelector);
  const isAddonsLoading = useAppSelector(loadingAddonsSelector);
  const sellableDays =
    useSelectorWithUrlParams(addonsAvailableSelector)?.user_management
      ?.standard ?? 0;
  const { isTrial, shouldShowFreeDuringTrialTag } = useTrialInfo();

  const [hasCompliedWithAgreement, setHasCompliedWithAgreement] =
    useState(false);

  const currentUserManagementFromApi = !currentAddons?.user_management
    ? "default"
    : (Object.keys(currentAddons.user_management)[0] as "standard");

  const [modalRadioSelection, setModalRadioSelection] = useState<AddonType>(
    currentUserManagementFromApi
  );

  const modalDirection =
    typeof currentAddons?.user_management?.standard === "number"
      ? "downgrading"
      : "upgrading";

  const daysRemaining = currentAddons?.user_management?.standard ?? 0;

  const currentText = intl.formatMessage({
    id: `organization.billing.modal.current`,
    defaultMessage: "(Current)"
  });

  useEffect(() => {
    if (organizationId && !currentAddons) {
      dispatch(getAddons({ organizationId }));
    }
  }, [dispatch, currentAddons, organizationId]);

  const updateAddonsHandler = async () => {
    const result = await dispatch(
      updateAddons({
        organizationId,
        payload: {
          user_management:
            modalRadioSelection === "default" ? null : modalRadioSelection
        }
      })
    );

    if (result?.type?.includes("fulfilled")) {
      setIsOpen(false);
      onUpgrade ? onUpgrade() : window.location.reload();
    }
  };

  const isActionButtonDisabled = useMemo(() => {
    // Don't allow user to update when radio selection is the same as the current value from api
    if (currentUserManagementFromApi === modalRadioSelection) {
      return true;
    }

    if (currentUserManagementFromApi === "default") {
      if (sellableDays === 0) {
        return false;
      }
      return !hasCompliedWithAgreement;
    }

    return !!daysRemaining;
  }, [
    daysRemaining,
    currentUserManagementFromApi,
    modalRadioSelection,
    hasCompliedWithAgreement,
    sellableDays
  ]);

  let actionButton: React.ReactNode;

  if (isAddonsLoading) {
    actionButton = (
      <S.LoadingActionWrapper>
        <Loading />
      </S.LoadingActionWrapper>
    );
  } else {
    actionButton = (
      <Button
        onClick={updateAddonsHandler}
        variant="primary"
        disabled={isActionButtonDisabled}
        analyticId={`organization.billing.${modalDirection}`}
      >
        {modalDirection &&
          intl.formatMessage({
            id: `organization.billing.${modalDirection}`
          })}
      </Button>
    );
  }

  return (
    <Modal
      size="medium"
      isOpen={isOpen && !!currentAddons}
      onOpenChange={() => setIsOpen(false)}
      handleClose={() => setIsOpen(false)}
      id="upgrade-add-on-modal"
    >
      <div>
        <Typography.Title tag="h3">
          {intl.formatMessage({
            id: "organization.billing.modal.title",
            defaultMessage: "User management add on"
          })}
        </Typography.Title>
        <S.ModalIntroText size="small" weight="regular">
          {intl.formatMessage({
            id: "organization.billing.modal.description",
            defaultMessage:
              "Upgrade today to enhance your user's collaboration with flexible permissions and teams, and strengthen your organization's security by enforcing MFA."
          })}
        </S.ModalIntroText>
        <S.RadioGroupWrapper>
          <Radio.Group
            value={modalRadioSelection}
            onValueChange={(value: typeof modalRadioSelection) =>
              setModalRadioSelection(value)
            }
          >
            {["default", "standard"].map(elt => (
              <Radio.Input
                key={`user-management-add-on-${elt}`}
                id={`user-management-add-on-${elt}`}
                value={elt}
                disabled={Boolean(elt === "default" && daysRemaining)}
                label={
                  <Typography.Label htmlFor={`user-management-add-on-${elt}`}>
                    <S.TitleTagWrapper>
                      <S.RadioMainText
                        size="medium"
                        weight="regular"
                        disabled={Boolean(elt === "default" && daysRemaining)}
                      >
                        {intl.formatMessage({
                          id: `organization.billing.modal.${elt}`
                        })}
                        {elt === currentUserManagementFromApi &&
                          ` ${currentText}`}
                      </S.RadioMainText>{" "}
                      {elt === "standard" && shouldShowFreeDuringTrialTag && (
                        <Tag variant="rose">
                          {intl.formatMessage({
                            id: "free_during_trial"
                          })}
                        </Tag>
                      )}
                    </S.TitleTagWrapper>
                    <Typography.BodyText size="small">
                      {intl.formatMessage(
                        {
                          id: `organization.billing.modal.${elt}.detail`
                        },
                        {
                          price:
                            organizationEstimate?.current.options
                              ?.standard_management_user?.formatted
                        }
                      )}
                    </Typography.BodyText>
                  </Typography.Label>
                }
              />
            ))}
          </Radio.Group>
        </S.RadioGroupWrapper>

        {modalDirection === "upgrading" && sellableDays > 0 && (
          <>
            <S.ModalDivider />
            <Checkbox
              disabled={false}
              label={intl.formatMessage({
                id: isTrial
                  ? "organization.billing.modal.comply.trial"
                  : "organization.billing.modal.comply",
                defaultMessage:
                  "I understand that I am committing to this upgrade for at least 30 days after my free trial has ended."
              })}
              checked={hasCompliedWithAgreement}
              onCheckedChanged={() =>
                setHasCompliedWithAgreement(prevState => !prevState)
              }
              id="organization.billing.modal.comply"
              forId="user-management-add-on-comply"
            />
          </>
        )}
        {modalDirection === "downgrading" && daysRemaining ? (
          <Typography.Label>
            {intl.formatMessage({
              id: "organization.billing.modal.note",
              defaultMessage:
                "Note: User management changes cannot be downgraded for 30 days."
            })}
          </Typography.Label>
        ) : null}
        <S.ActionWrapper>
          {modalDirection === "downgrading" && daysRemaining ? (
            <S.MtcNote>
              {intl.formatMessage(
                {
                  id: "organization.billing.modal.remaining"
                },
                {
                  days: daysRemaining
                }
              )}
            </S.MtcNote>
          ) : null}
          <S.ButtonWrapper>
            <Button
              analyticId="cancel"
              variant="secondary"
              onClick={() => setIsOpen(false)}
            >
              {intl.formatMessage({
                id: "cancel",
                defaultMessage: "Cancel"
              })}
            </Button>
            {actionButton}
          </S.ButtonWrapper>
        </S.ActionWrapper>
        {addonsError && (
          <S.StickerRoot priority="critical">
            <Sticker.Icon priority="critical" />
            <Sticker.Text>{addonsError}</Sticker.Text>
          </S.StickerRoot>
        )}
      </div>
    </Modal>
  );
};
